import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, MapPin, Filter, Download } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Skeleton from '../../components/ui/Skeleton';
import { cn } from '../../utils/cn';
import marketAnalysisService from '../../services/marketAnalysisService';
import { communeService } from '../../services/communeService';
import EmptyState from '../../components/ui/EmptyState';

// ── Zinc Card primitives ──────────────────────────────────────────────────────
const Card = ({ className = '', children }) => (
  <div className={cn("rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm", className)}>
    {children}
  </div>
);
const CardHeader = ({ className = '', children }) => (
  <div className={cn("flex items-center gap-2 px-6 py-4 border-b border-zinc-100 dark:border-zinc-800", className)}>
    {children}
  </div>
);
const CardContent = ({ className = '', children }) => (
  <div className={cn("p-6", className)}>{children}</div>
);
// ─────────────────────────────────────────────────────────────────────────────

const MarketAnalysisDashboard = () => {
  const [marketStats, setMarketStats] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [selectedCommunes, setSelectedCommunes] = useState([]);
  const [marketTrends, setMarketTrends] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [statsData, communesData] = await Promise.all([
        marketAnalysisService?.getMarketStats(),
        communeService?.getAllCommunes()
      ]);
      const communesResult = Array.isArray(communesData) ? communesData : communesData?.data || [];
      setMarketStats(statsData || []);
      setCommunes(communesResult);
      if (communesResult?.length > 0) {
        setSelectedCommune(communesResult?.[0]?.id);
        await loadCommuneTrends(communesResult?.[0]?.id);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données de marché');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCommuneTrends = async (communeId, period = 'monthly') => {
    if (!communeId) return;
    try {
      const trendsData = await marketAnalysisService?.getCommuneMarketTrends(communeId, period);
      setMarketTrends(trendsData || []);
    } catch (err) {
      setError('Erreur lors du chargement des tendances');
      console.error('Erreur:', err);
    }
  };

  const handleCommuneChange = (communeId) => {
    setSelectedCommune(communeId);
    loadCommuneTrends(communeId, selectedPeriod);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (selectedCommune) loadCommuneTrends(selectedCommune, period);
  };

  const compareCommunes = async () => {
    if (selectedCommunes?.length < 2) {
      setError('Veuillez sélectionner au moins 2 communes pour la comparaison');
      return;
    }
    setLoading(true);
    try {
      const comparison = await marketAnalysisService?.compareCommuneMarkets(selectedCommunes);
      setComparisonData(comparison || []);
    } catch (err) {
      setError('Erreur lors de la comparaison des marchés');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommuneSelectionToggle = (communeId) => {
    setSelectedCommunes(prev =>
      prev?.includes(communeId) ? prev?.filter(id => id !== communeId) : [...prev, communeId]
    );
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR')?.format(price);
  const formatPercentage = (value) => `${value?.toFixed(1)}%`;

  const chartColors = ['#3f3f46', '#71717a', '#a1a1aa', '#d4d4d8', '#52525b', '#18181b'];

  const trendChartData = marketTrends?.map(trend => ({
    date: new Date(trend?.analysis_date)?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
    price: trend?.average_price_per_sqm,
    change: trend?.price_change_percentage,
    volume: trend?.transaction_volume
  })) || [];

  const marketDistributionData = marketStats?.map((stat, index) => ({
    name: stat?.market_type === 'sale' ? 'Vente' : 'Location',
    value: stat?.total_count,
    color: chartColors?.[index % chartColors?.length]
  })) || [];

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 py-8">
      <div className="max-w-7xl mx-auto px-4">

        {/* ── Header ── */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-3">
            <BarChart3 className="h-10 w-10 text-zinc-700 dark:text-zinc-300 mr-3" />
            <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              Analyse de Marché Immobilier
            </h1>
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-3xl mx-auto">
            Intelligence de marché en temps réel pour les investissements immobiliers à Abidjan
          </p>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="mb-6 p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* ── KPI Cards ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)]?.map((_, i) => (
              <Card key={i}>
                <div className="p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-7 w-20" />
                    </div>
                    <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
                  </div>
                  <div className="space-y-2 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {marketStats?.map((stat) => (
              <Card key={stat?.market_type}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                        {stat?.market_type === 'sale' ? 'Marché Vente' : 'Marché Location'}
                      </p>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 tabular-nums mt-0.5">
                        {stat?.total_count}{' '}
                        <span className="text-base font-medium text-zinc-400 dark:text-zinc-500">biens</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 flex-shrink-0">
                      {stat?.market_type === 'sale'
                        ? <TrendingUp className="h-5 w-5" />
                        : <TrendingDown className="h-5 w-5" />
                      }
                    </div>
                  </div>
                  <div className="space-y-2 text-sm border-t border-zinc-100 dark:border-zinc-800 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-500 dark:text-zinc-400">Prix moyen</span>
                      <span className="font-semibold text-zinc-800 dark:text-zinc-200 tabular-nums">
                        {formatPrice(stat?.avg_price)} FCFA
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-500 dark:text-zinc-400">Min – Max</span>
                      <span className="font-medium text-zinc-600 dark:text-zinc-400 tabular-nums text-xs">
                        {formatPrice(stat?.min_price)} – {formatPrice(stat?.max_price)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── Filtering Controls ── */}
        <Card className="mb-8">
          <CardHeader>
            <Filter className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
            <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-50 flex-1">
              Filtres et Analyse
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter Rapport
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select
                label="Commune à analyser"
                value={selectedCommune}
                onChange={(val) => handleCommuneChange(val)}
                options={[
                  { value: '', label: 'Sélectionner une commune' },
                  ...(communes?.map(commune => ({
                    value: commune?.id,
                    label: commune?.name
                  })) || [])
                ]}
              />

              <Select
                label="Période d'analyse"
                value={selectedPeriod}
                onChange={(val) => handlePeriodChange(val)}
                options={[
                  { value: 'monthly', label: 'Mensuelle' },
                  { value: 'quarterly', label: 'Trimestrielle' },
                  { value: 'yearly', label: 'Annuelle' }
                ]}
              />

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Comparer les Communes
                </label>
                <Button
                  onClick={compareCommunes}
                  disabled={selectedCommunes?.length < 2 || loading}
                  variant="outline"
                  className="w-full border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-40"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Comparer ({selectedCommunes?.length})
                </Button>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Communes à comparer
                </label>
                <div className="max-h-32 overflow-y-auto border border-zinc-200 dark:border-zinc-700 rounded-md p-2 bg-white dark:bg-zinc-900">
                  {communes?.map(commune => (
                    <label key={commune?.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:text-zinc-900 dark:hover:text-zinc-100 text-zinc-600 dark:text-zinc-400">
                      <input
                        type="checkbox"
                        checked={selectedCommunes?.includes(commune?.id)}
                        onChange={() => handleCommuneSelectionToggle(commune?.id)}
                        className="h-3.5 w-3.5 rounded border-zinc-300 dark:border-zinc-600 accent-zinc-700"
                      />
                      {commune?.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Chart Containers ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Price Trends Chart */}
          <Card>
            <CardHeader>
              <TrendingUp className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                Évolution des Prix
              </h3>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-end gap-2 h-[300px] px-2">
                    {[...Array(7)]?.map((_, i) => (
                      <Skeleton key={i} className="flex-1 rounded-t-md" style={{ height: `${40 + Math.random() * 60}%` }} />
                    ))}
                  </div>
                  <div className="flex justify-between">
                    {[...Array(7)]?.map((_, i) => (
                      <Skeleton key={i} className="h-3 w-10" />
                    ))}
                  </div>
                </div>
              ) : trendChartData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#71717a' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#71717a' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px', fontSize: '12px' }}
                      formatter={(value, name) => [
                        name === 'price' ? `${formatPrice(value)} FCFA/m²` : `${value}%`,
                        name === 'price' ? 'Prix/m²' : 'Variation'
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line type="monotone" dataKey="price" stroke="#3f3f46" strokeWidth={2.5} dot={false} name="Prix/m²" />
                    <Line type="monotone" dataKey="change" stroke="#a1a1aa" strokeWidth={2} dot={false} strokeDasharray="4 2" name="Variation %" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  iconName="TrendingUp"
                  title="Aucune tendance disponible"
                  description="Sélectionnez une commune pour afficher l'évolution des prix."
                  size="sm"
                />
              )}
            </CardContent>
          </Card>

          {/* Market Distribution */}
          <Card>
            <CardHeader>
              <MapPin className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                Répartition du Marché
              </h3>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
                  <Skeleton className="h-48 w-48 rounded-full" />
                  <div className="flex gap-4">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              ) : marketDistributionData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={marketDistributionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    >
                      {marketDistributionData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  iconName="PieChart"
                  title="Données indisponibles"
                  description="Chargement des données de répartition du marché en cours."
                  size="sm"
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Commune Comparison Table ── */}
        {comparisonData?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <BarChart3 className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                Comparaison des Communes
              </h3>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="commune_name" tick={{ fontSize: 12, fill: '#71717a' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#71717a' }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(value, name) => [
                          name === 'avg_price' ? `${formatPrice(value)} FCFA` :
                          name === 'rental_yield' ? `${value?.toFixed(1)}%` :
                          name === 'market_score' ? `${value}/100` : value,
                          name === 'avg_price' ? 'Prix Moyen' :
                          name === 'rental_yield' ? 'Rendement Locatif' :
                          name === 'market_score' ? 'Score Marché' : name
                        ]}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="market_score" fill="#52525b" name="Score Marché" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="rental_yield" fill="#a1a1aa" name="Rendement %" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 dark:border-zinc-800">
                        <th className="text-left py-3 px-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Commune</th>
                        <th className="text-right py-3 px-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Prix Moyen</th>
                        <th className="text-right py-3 px-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Tendance</th>
                        <th className="text-right py-3 px-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Rendement</th>
                        <th className="text-right py-3 px-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                      {comparisonData?.map((commune) => (
                        <tr key={commune?.commune_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                          <td className="py-3 px-2 font-medium text-zinc-900 dark:text-zinc-100">{commune?.commune_name}</td>
                          <td className="text-right py-3 px-2 text-zinc-700 dark:text-zinc-300 tabular-nums">{formatPrice(commune?.avg_price)} FCFA</td>
                          <td className={cn(
                            "text-right py-3 px-2 font-semibold tabular-nums",
                            commune?.price_trend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                          )}>
                            {commune?.price_trend >= 0 ? '+' : ''}{formatPercentage(commune?.price_trend)}
                          </td>
                          <td className="text-right py-3 px-2 text-zinc-700 dark:text-zinc-300 tabular-nums">{formatPercentage(commune?.rental_yield)}</td>
                          <td className="text-right py-3 px-2">
                            <span className={cn(
                              "inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold tabular-nums",
                              commune?.market_score >= 70
                                ? 'bg-zinc-800 text-zinc-100 dark:bg-zinc-200 dark:text-zinc-900'
                                : commune?.market_score >= 50
                                ? 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200' :'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                            )}>
                              {commune?.market_score?.toFixed(0)}/100
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Market Intelligence ── */}
        <Card className="mt-6 border-zinc-800 dark:border-zinc-700 bg-zinc-900 dark:bg-zinc-950">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-zinc-100 mb-4">
              Intelligence de Marché — Points Clés
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-zinc-300 mb-1.5">🏆 Meilleur Investissement</h4>
                <p className="text-sm text-zinc-400">
                  {comparisonData?.length > 0
                    ? `${comparisonData?.reduce((best, current) =>
                        current?.market_score > best?.market_score ? current : best)?.commune_name || 'N/A'} avec un score de ${comparisonData?.reduce((best, current) =>
                        current?.market_score > best?.market_score ? current : best)?.market_score?.toFixed(0) || 0}/100`
                    : 'Effectuez une comparaison pour voir les recommandations'
                  }
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-zinc-300 mb-1.5">📈 Tendance Générale</h4>
                <p className="text-sm text-zinc-400">
                  Le marché immobilier d'Abidjan montre une croissance stable avec des opportunités d'investissement dans plusieurs communes.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-zinc-300 mb-1.5">💰 Rendement Moyen</h4>
                <p className="text-sm text-zinc-400">
                  {comparisonData?.length > 0
                    ? `${(comparisonData?.reduce((sum, commune) => sum + commune?.rental_yield, 0) / comparisonData?.length)?.toFixed(1)}% de rendement locatif moyen`
                    : 'Données en cours de calcul...'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Integration Note ── */}
        <Card className="mt-6 border-zinc-200 dark:border-zinc-800">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50 mb-2">
              🔗 Intégration avec vos Analyses
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
              Utilisez ces données de marché pour optimiser vos calculs ROI et identifier les meilleures opportunités d'investissement.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/roi-calculator-tool'}
                className="border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                Calculer ROI
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/property-search-listings'}
                className="border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                Voir Propriétés
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default MarketAnalysisDashboard;